import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Les trois sections de la page d'accueil, dans leur ordre d'affichage. */
export type TreeSectionId = 'favorites' | 'recent' | 'spaces';

interface TreeSectionsState {
  /** Sections repliées. Absente de l'objet = dépliée. */
  collapsed: Partial<Record<TreeSectionId, boolean>>;
  toggle: (id: TreeSectionId) => void;
}

/**
 * Sections repliées de la page d'accueil, **persistées**.
 *
 * Persisté et non gardé en mémoire, parce que c'est tout l'intérêt : le volet
 * Outlook est démonté et remonté sans cesse — à chaque changement de mail
 * sélectionné, à chaque fermeture du volet. Un état de repli en mémoire serait
 * perdu au premier de ces cycles, et replier « Espaces de service » n'aurait
 * jamais l'air de tenir.
 *
 * Store séparé d'`uiStore` : la disposition des sections n'a rien à voir avec la
 * navigation, et celle-ci est déjà persistée sous une autre clé avec ses propres
 * règles de migration.
 */
export const useTreeSectionsStore = create<TreeSectionsState>()(
  persist(
    (set) => ({
      // Tout est déplié au premier lancement : replier est un geste que
      // l'utilisateur pose, jamais un défaut qui lui cacherait ses favoris.
      collapsed: {},
      toggle: (id) =>
        set((s) => ({ collapsed: { ...s.collapsed, [id]: !s.collapsed[id] } })),
    }),
    {
      name: 'prism-tree-sections',
      // Une valeur persistée abîmée (clé écrite à la main, ancienne version) ne
      // doit pas pouvoir masquer une section pour toujours : on ne retient que
      // les trois identifiants connus, et rien d'autre.
      merge: (persisted, current) => {
        if (!persisted || typeof persisted !== 'object') return current;
        const raw = (persisted as Partial<TreeSectionsState>).collapsed;
        const collapsed: Partial<Record<TreeSectionId, boolean>> = {};
        if (raw && typeof raw === 'object') {
          for (const id of ['favorites', 'recent', 'spaces'] as TreeSectionId[]) {
            if (raw[id] === true) collapsed[id] = true;
          }
        }
        return { ...current, collapsed };
      },
    },
  ),
);
